import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { FileDown, FileText, AlertTriangle, CheckCircle2, DollarSign, Files, Settings2 } from "lucide-react";
import { API } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { Link } from "react-router-dom";

export default function Tax1099Page() {
  const { active } = useProperty();
  const propertyId = active?.id;
  const [year, setYear] = useState(new Date().getFullYear() - 1);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filerReady, setFilerReady] = useState(false);
  const [filerErrors, setFilerErrors] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = propertyId ? `&property_id=${propertyId}` : "";
      const [r, f] = await Promise.all([
        api.get(`/admin/tax/1099?year=${year}${q}`),
        propertyId
          ? api.get(`/admin/tax/1099-misc/filer/${propertyId}`).catch(() => ({ data: null }))
          : Promise.resolve({ data: null }),
      ]);
      setReport(r.data);
      if (f.data) {
        setFilerReady(!!f.data.ready_to_file);
        setFilerErrors(f.data.validation_errors || []);
      } else {
        setFilerReady(false);
        setFilerErrors([]);
      }
    } catch (e) { toast.error(apiErrorMessage(e)); setReport(null); }
    setLoading(false);
  }, [year, propertyId]);
  useEffect(() => { load(); }, [load]);

  const csvUrl = `${API}/admin/tax/1099.csv?year=${year}${propertyId ? `&property_id=${propertyId}` : ""}`;
  const pdfUrl = `${API}/admin/tax/1099.pdf?year=${year}${propertyId ? `&property_id=${propertyId}` : ""}`;

  const dlBinary = async (path, mime, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      const cnt = res.headers?.["x-payee-count"];
      const excl = res.headers?.["x-excluded-no-tin"];
      if (cnt) {
        toast.success(`Downloaded — ${cnt} record(s)${Number(excl) ? ` · ${excl} excluded (no full TIN)` : ""}`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.statusText || "Download failed";
      toast.error(String(msg).slice(0, 240));
    }
  };
  const downloadFire = () => dlBinary(`/admin/tax/1099.fire.txt?year=${year}&property_id=${propertyId}`, "text/plain", `IRSTAX-1099NEC-${year}.txt`);
  const downloadIrisXml = () => dlBinary(`/admin/tax/1099.iris.xml?year=${year}&property_id=${propertyId}`, "application/xml", `IRIS-1099NEC-${year}.xml`);
  const downloadIrisCsv = () => dlBinary(`/admin/tax/1099.iris.csv?year=${year}&property_id=${propertyId}`, "text/csv", `IRIS-1099NEC-${year}.csv`);

  return (
    <div>
      <PageHeader eyebrow="Tax" title="1099-NEC Report" subtitle="Year-end vendor payment aggregation. IRS threshold $600 — reportable vendors need a W-9 on file before filing." />
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Input data-testid="year-input" type="number" min="2020" max="2099" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10) || year)} className="w-32 bg-white/[0.04] border-white/10" />
        <Button data-testid="run-btn" onClick={load} disabled={loading} className="bg-orange-500 hover:bg-orange-600">{loading ? "Running…" : "Run report"}</Button>
        <div className="flex-1" />
        <a href={csvUrl} target="_blank" rel="noreferrer" data-testid="dl-csv" className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full border border-white/10 text-zinc-300 hover:bg-white/5"><FileDown className="h-3 w-3" /> CSV</a>
        <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="dl-pdf" className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full border border-orange-500/40 text-orange-300 bg-orange-500/10 hover:bg-orange-500/20"><FileText className="h-3 w-3" /> PDF</a>
        <Button variant="outline" onClick={downloadFire} disabled={!propertyId}
                className="gap-1 border-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="download-nec-fire">
          <Files size={13} /> FIRE .txt (TY ≤ 2025)
        </Button>
        <Button onClick={downloadIrisXml} disabled={!propertyId}
                className="gap-1 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="download-nec-iris-xml">
          <Files size={13} /> IRIS XML (TY 2026+)
        </Button>
        <Button variant="outline" onClick={downloadIrisCsv} disabled={!propertyId}
                className="gap-1 border-indigo-500/40 text-indigo-300 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="download-nec-iris-csv">
          <Files size={13} /> IRIS CSV
        </Button>
      </div>

      {!filerReady && (
        <Card className="border-amber-500/30 bg-amber-500/[0.04] p-3 mb-4" data-testid="nec-filer-warning">
          <div className="flex items-start gap-2 text-[12px] text-amber-200">
            <Settings2 className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              IRS filer info not yet complete — FIRE / IRIS export locked. Configure your TCC and payer address on the{" "}
              <Link to="/admin/tax/1099-misc" className="underline decoration-dotted underline-offset-2">1099-MISC page</Link>{" "}
              (shared with 1099-NEC).
              {filerErrors.length > 0 && (
                <span className="block text-[10px] text-amber-300 mt-0.5">Missing: {filerErrors.slice(0, 3).join(" · ")}</span>
              )}
            </div>
          </div>
        </Card>
      )}

      {report && (
        <>
          <div className="grid grid-cols-4 gap-3 mb-4">
            <Card className="border-white/10 bg-card p-3"><div className="overline text-[10px]">Total vendors</div><div className="text-2xl font-semibold" data-testid="stat-total">{report.total_vendors}</div></Card>
            <Card className="border-white/10 bg-card p-3"><div className="overline text-[10px]">Reportable (≥ ${report.threshold_usd})</div><div className="text-2xl font-semibold text-orange-300" data-testid="stat-reportable">{report.reportable_vendors}</div></Card>
            <Card className="border-white/10 bg-card p-3"><div className="overline text-[10px]">Missing W-9</div><div className={`text-2xl font-semibold ${report.missing_w9_count ? "text-rose-300" : "text-emerald-300"}`} data-testid="stat-missing-w9">{report.missing_w9_count}</div></Card>
            <Card className="border-white/10 bg-card p-3"><div className="overline text-[10px]">Reportable total</div><div className="text-2xl font-semibold"><DollarSign className="h-4 w-4 inline -mt-0.5" />{report.total_paid_reportable_usd.toLocaleString()}</div></Card>
          </div>
          {report.missing_w9_count > 0 && (
            <div className="rounded-md border border-rose-500/30 bg-rose-500/5 px-3 py-2 text-[11px] text-rose-200 mb-3" data-testid="w9-warning">
              <AlertTriangle className="h-3.5 w-3.5 inline mr-1 -mt-0.5" /> {report.missing_w9_count} reportable vendor(s) are missing a W-9. Collect W-9s via <a href="/admin/vendor-compliance" className="underline">Vendor Compliance</a> before filing.
            </div>
          )}
          <Card className="border-white/10 bg-card p-4">
            <div className="space-y-2">
              {report.items.map((r) => (
                <div key={r.vendor_id} className="rounded border border-white/10 bg-white/[0.02] p-3 flex items-center justify-between" data-testid={`row-${r.vendor_id}`}>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{r.vendor_name}</div>
                    <div className="text-xs text-zinc-500 mt-1">{r.bill_count} bills · EIN ***-**-{r.vendor_ein_last4 || "????"} · {r.vendor_address || "no address"}</div>
                  </div>
                  <div className="flex items-center gap-3 text-right">
                    <div>
                      <div className="text-sm font-semibold">${r.total_paid_usd.toLocaleString()}</div>
                      <div className="text-[10px] text-zinc-500">total paid</div>
                    </div>
                    {r.above_threshold ? (
                      r.w9_on_file
                        ? <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10"><CheckCircle2 className="h-3 w-3 mr-1" /> Ready to file</Badge>
                        : <Badge variant="outline" className="border-rose-500/40 text-rose-300 bg-rose-500/10"><AlertTriangle className="h-3 w-3 mr-1" /> W-9 required</Badge>
                    ) : <Badge variant="outline" className="border-white/10 text-zinc-500">Below threshold</Badge>}
                  </div>
                </div>
              ))}
              {report.items.length === 0 && <div className="text-sm text-muted-foreground py-8 text-center">No paid vendor bills in {year}.</div>}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
